import type {
	HistoryDetailDto,
	HistoryDto,
	StructureDetailDto,
	StructureDto,
} from '@/endpoints/models'
import type {
	HistoryWithDeploymentDto,
	PageWrapperHistoryWithDeploymentDto,
	StructureMetadataDtoRead,
	StructureReferenceInfoDto,
} from '@/rtkApi/mddApiSliceGeneratedV2'
import { isPrivilegedUser } from '@/utils/locking'

const ALL_STRUCTURE_PERMISSIONS = [
	'VIEW',
	'EDIT',
	'MANAGE_ACCESS',
	'FORCE_UNLOCK',
] as const

/**
 * Adapts GET /v2/branch/{branchId}/structure/{id} metadata into the legacy
 * StructureDetailDto shape used by Redux NODE_LOAD. Large/sensitive fields
 * (data, workingData) stay unset until feature-specific loaders fetch them.
 */
export function mapStructureMetadataToStructureDetailDto(
	meta: StructureMetadataDtoRead,
): StructureDetailDto {
	const privileged = isPrivilegedUser()
	const permissions =
		privileged &&
		(!Array.isArray(meta.permissions) || meta.permissions.length === 0)
			? [...ALL_STRUCTURE_PERMISSIONS]
			: meta.permissions
	// If the API omits `permissions`, do not assume edit is allowed (VIEW-only was incorrectly treated as editable).
	const canEdit =
		privileged ||
		(Array.isArray(permissions) && permissions.includes('EDIT'))

	return {
		id: meta.id ?? 0,
		type: (meta.type ?? 'FOLDER') as StructureDetailDto.TypeEnum,
		name: meta.name ?? '',
		code: meta.code ?? '',
		uuid: meta.uuid,
		parentStructureId: meta.parent?.id,
		hasChildren: false,
		companyId: 0,
		canEdit,
		structurePermissions: permissions,
		lockUserId: meta.lockedFor?.id,
		lockUserName: meta.lockedFor?.fullName,
		lockTime: meta.lockedTime,
		orderPriority: meta.orderPriority,
		status: meta.status,
	}
}

export function mapStructureMetadataToStructureDto(
	meta: StructureMetadataDtoRead,
): StructureDto {
	const detail = mapStructureMetadataToStructureDetailDto(meta)

	return {
		id: detail.id,
		type: detail.type as StructureDto['type'],
		name: detail.name,
		code: detail.code,
		uuid: detail.uuid,
		parentStructureId: detail.parentStructureId,
		hasChildren: detail.hasChildren,
		companyId: detail.companyId,
		canEdit: detail.canEdit,
		structurePermissions: detail.structurePermissions,
		lockUserId: detail.lockUserId,
		lockUserName: detail.lockUserName,
		lockTime: detail.lockTime,
		orderPriority: detail.orderPriority,
		status: detail.status,
		fullPath: meta.fullPath,
	}
}

export function mapStructureReferenceToStructureDto(
	ref: StructureReferenceInfoDto,
): StructureDto {
	return mapStructureMetadataToStructureDto({
		id: ref.id,
		name: ref.name,
		code: ref.code,
		type: ref.type,
		uuid: ref.uuid,
		fullPath: ref.fullPath,
	})
}

export function mapHistoryWithDeploymentToHistoryDto(
	item: {
		authorId: number
		authorName: string
		created: string
		description?: string
		id: number
		structure?: StructureReferenceInfoDto
		version: number
	},
): HistoryDto {
	return {
		id: item.id,
		version: item.version,
		description: item.description,
		structureId: item.structure?.id ?? 0,
		authorId: item.authorId,
		authorName: item.authorName,
		created: item.created,
	}
}

export function mapHistoryPageToHistoryDtoList(
	page: PageWrapperHistoryWithDeploymentDto,
): HistoryDto[] {
	return (page.content ?? []).map(mapHistoryWithDeploymentToHistoryDto)
}

export function mapHistoryWithDeploymentToHistoryDetailDto(
	item: HistoryWithDeploymentDto,
): HistoryDetailDto {
	return {
		...mapHistoryWithDeploymentToHistoryDto(item),
		name: item.structure?.name,
		code: item.structure?.code,
		data: JSON.stringify(item),
	}
}

export const toCreateChildStructureType = (type: string) => {
	if (type === 'FOLDER_WORKFLOW') return 'FOLDER'
	if (type === 'MAPPING_DEFAULT') return 'MAPPING'
	return type
}
